import { DIRECTIONS, manhattan, step, type TilePos } from './geometry.js';
import { type CollisionGrid } from './grid.js';

export interface FindPathOptions {
  /** Safety cap on expanded nodes; protects the server tick from pathological requests. */
  readonly maxExpanded?: number;
}

const DEFAULT_MAX_EXPANDED = 20_000;

/**
 * 4-directional A* on the collision grid.
 *
 * @returns the tiles to walk, **excluding** `start` and **including** `goal`;
 *          an empty array if start equals goal; `null` if no path exists.
 */
export function findPath(
  grid: CollisionGrid,
  start: TilePos,
  goal: TilePos,
  options: FindPathOptions = {},
): TilePos[] | null {
  if (!grid.isWalkable(start) || !grid.isWalkable(goal)) return null;
  if (start.x === goal.x && start.y === goal.y) return [];

  const maxExpanded = options.maxExpanded ?? DEFAULT_MAX_EXPANDED;
  const size = grid.width * grid.height;
  const gScore = new Float64Array(size).fill(Infinity);
  const cameFrom = new Int32Array(size).fill(-1);
  const closed = new Uint8Array(size);
  const open = new MinHeap();

  const startIndex = grid.index(start);
  const goalIndex = grid.index(goal);
  gScore[startIndex] = 0;
  open.push(startIndex, manhattan(start, goal));

  let expanded = 0;
  while (open.size > 0) {
    const current = open.pop();
    if (current === goalIndex) return reconstruct(grid, cameFrom, current, startIndex);
    if (closed[current] === 1) continue;
    closed[current] = 1;
    if (++expanded > maxExpanded) return null;

    const currentPos = grid.fromIndex(current);
    const currentG = gScore[current] ?? Infinity;
    for (const direction of DIRECTIONS) {
      const next = step(currentPos, direction);
      if (!grid.isWalkable(next)) continue;
      const nextIndex = grid.index(next);
      if (closed[nextIndex] === 1) continue;
      const tentative = currentG + 1;
      if (tentative < (gScore[nextIndex] ?? Infinity)) {
        gScore[nextIndex] = tentative;
        cameFrom[nextIndex] = current;
        open.push(nextIndex, tentative + manhattan(next, goal));
      }
    }
  }
  return null;
}

function reconstruct(
  grid: CollisionGrid,
  cameFrom: Int32Array,
  goalIndex: number,
  startIndex: number,
): TilePos[] {
  const path: TilePos[] = [];
  let current = goalIndex;
  while (current !== startIndex) {
    path.push(grid.fromIndex(current));
    current = cameFrom[current] ?? -1;
    if (current < 0) throw new Error('Corrupt path reconstruction');
  }
  return path.reverse();
}

/** Binary min-heap of (index, priority). Ties are broken by insertion order for deterministic paths. */
interface HeapItem {
  readonly index: number;
  readonly priority: number;
  readonly order: number;
}

class MinHeap {
  private readonly items: HeapItem[] = [];
  private counter = 0;

  get size(): number {
    return this.items.length;
  }

  push(index: number, priority: number): void {
    this.items.push({ index, priority, order: this.counter++ });
    this.bubbleUp(this.items.length - 1);
  }

  pop(): number {
    const top = this.items[0];
    const last = this.items.pop();
    if (!top || !last) throw new Error('pop() on empty heap');
    if (this.items.length > 0) {
      this.items[0] = last;
      this.sinkDown(0);
    }
    return top.index;
  }

  private at(i: number): HeapItem {
    const item = this.items[i];
    if (!item) throw new RangeError(`Heap index ${i} out of range`);
    return item;
  }

  private less(a: number, b: number): boolean {
    const x = this.at(a);
    const y = this.at(b);
    return x.priority < y.priority || (x.priority === y.priority && x.order < y.order);
  }

  private swap(a: number, b: number): void {
    const tmp = this.at(a);
    this.items[a] = this.at(b);
    this.items[b] = tmp;
  }

  private bubbleUp(i: number): void {
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (!this.less(i, parent)) return;
      this.swap(i, parent);
      i = parent;
    }
  }

  private sinkDown(i: number): void {
    const n = this.items.length;
    for (;;) {
      const left = 2 * i + 1;
      const right = left + 1;
      let smallest = i;
      if (left < n && this.less(left, smallest)) smallest = left;
      if (right < n && this.less(right, smallest)) smallest = right;
      if (smallest === i) return;
      this.swap(i, smallest);
      i = smallest;
    }
  }
}
